import { useState } from "react";
import { ethers } from "ethers";
import { Upload, Clock, Users, Plus, X, FileText, CheckCircle, AlertCircle, ArrowLeft } from "lucide-react";
import { contractService } from "../services/contractService";
import { pinataService } from "../services/pinataService";
import { validateInput, handleError } from "../utils/errorHandler";
import { CONFIG } from "../config";
import ModernNavbar from "./ModernNavbar";

export default function ModernRegisterWill({ onBack }) {
  const [file, setFile] = useState(null);
  const [interval, setInterval] = useState(CONFIG.DEFAULT_HEARTBEAT_INTERVAL.toString());
  const [quorum, setQuorum] = useState(CONFIG.DEFAULT_QUORUM);
  const [beneficiaries, setBeneficiaries] = useState([""]);
  const [policyId, setPolicyId] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [cid, setCid] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const uploadToPinata = async () => {
    try {
      validateInput.file(file);
      
      setStatus({ type: "loading", message: "Uploading file to IPFS..." });
      const result = await pinataService.uploadFile(file, {
        name: file.name,
        description: `Will document uploaded on ${new Date().toISOString()}`
      });
      
      setCid(result.cid);
      setStatus({ type: "success", message: `File uploaded successfully: ${result.cid.substring(0, 20)}...` });
      return result.cid;
    } catch (error) {
      const errorInfo = handleError(error, 'File Upload');
      setStatus({ type: "error", message: errorInfo.userMessage });
      return null;
    }
  };

  const addBeneficiary = () => {
    setBeneficiaries([...beneficiaries, ""]);
  };

  const removeBeneficiary = (index) => {
    if (beneficiaries.length > 1) {
      setBeneficiaries(beneficiaries.filter((_, i) => i !== index));
    }
  };

  const updateBeneficiary = (index, value) => {
    const updated = [...beneficiaries];
    updated[index] = value;
    setBeneficiaries(updated);
  };

  const registerPolicy = async () => {
    try {
      setIsLoading(true);
      
      // Validate inputs
      validateInput.file(file);
      validateInput.heartbeatInterval(interval);
      validateInput.quorum(quorum);
      
      const validBeneficiaries = beneficiaries.filter(addr => addr.trim() !== "");
      if (validBeneficiaries.length === 0) {
        throw new Error("At least one beneficiary is required");
      }
      
      validBeneficiaries.forEach(addr => validateInput.address(addr));

      // Upload file
      const cid = await uploadToPinata();
      if (!cid) return;

      // Generate policy ID
      setStatus({ type: "loading", message: "Generating policy ID..." });
      const ownerAddress = await contractService.getSignerAddress();
      const timestamp = Date.now().toString();
      const newPolicyId = ethers.id(ownerAddress + timestamp);

      // Register on blockchain
      setStatus({ type: "loading", message: "Submitting transaction to blockchain..." });
      await contractService.registerPolicy(
        newPolicyId,
        cid,
        Number(interval),
        Number(quorum),
        validBeneficiaries
      );

      // Store locally
      const newPolicy = {
        policyId: newPolicyId,
        cid,
        interval: Number(interval),
        quorum: Number(quorum),
        beneficiaries: validBeneficiaries,
        createdAt: Date.now()
      };
      
      const existing = JSON.parse(localStorage.getItem("willguard_policies")) || [];
      localStorage.setItem("willguard_policies", JSON.stringify([newPolicy, ...existing]));

      setPolicyId(newPolicyId);
      setStatus({ type: "success", message: "Policy registered successfully!" });
      setCurrentStep(4);
      
    } catch (error) {
      const errorInfo = handleError(error, 'Policy Registration');
      setStatus({ type: "error", message: errorInfo.userMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setInterval(CONFIG.DEFAULT_HEARTBEAT_INTERVAL.toString());
    setQuorum(CONFIG.DEFAULT_QUORUM);
    setBeneficiaries([""]);
    setPolicyId("");
    setStatus({ type: "", message: "" });
    setCid("");
    setCurrentStep(1);
  };

  const steps = [
    { number: 1, title: "Document", icon: <FileText className="w-5 h-5" /> },
    { number: 2, title: "Settings", icon: <Clock className="w-5 h-5" /> },
    { number: 3, title: "Beneficiaries", icon: <Users className="w-5 h-5" /> },
    { number: 4, title: "Review", icon: <CheckCircle className="w-5 h-5" /> }
  ];

  return (
    <>
      <ModernNavbar />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <FileText className="w-8 h-8 text-indigo-600" />
            Register New Will
          </h1>
          <p className="text-gray-600">Secure your digital legacy on the blockchain</p>
        </div>

        {policyId ? (
          // Success Screen
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Policy Registered Successfully!</h2>
              <p className="text-gray-600 mb-6">Your digital will has been securely stored on the blockchain</p>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <p className="text-sm text-gray-600 mb-2 font-semibold">Policy ID</p>
                <p className="font-mono text-sm text-gray-800 break-all mb-4">{policyId}</p>
                <button
                  onClick={() => navigator.clipboard.writeText(policyId)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Copy to Clipboard
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-800 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>Remember to send regular heartbeats to keep your will active. You can manage this from your dashboard.</span>
                </p>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={onBack}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Go to Dashboard
                </button>
                <button
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                >
                  Register Another
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Progress Steps */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-200">
              <div className="flex items-center justify-between">
                {steps.map((step, index) => (
                  <div key={step.number} className="flex items-center flex-1">
                    <div className={`flex items-center gap-3 ${index < steps.length - 1 ? 'flex-1' : ''}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                        currentStep >= step.number
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {currentStep > step.number ? <CheckCircle className="w-5 h-5" /> : step.number}
                      </div>
                      <div className="hidden md:block">
                        <p className={`text-sm font-semibold ${currentStep >= step.number ? 'text-gray-900' : 'text-gray-500'}`}>
                          {step.title}
                        </p>
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`h-1 flex-1 mx-4 rounded transition-all ${
                        currentStep > step.number ? 'bg-gradient-to-r from-indigo-600 to-purple-600' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
              
              {/* Step 1: Document Upload */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Upload Your Will Document</h3>
                    <p className="text-gray-600 mb-6">Select the document you want to store securely on IPFS</p>
                  </div>

                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-indigo-400 transition-colors">
                    <input
                      id="file-upload"
                      type="file"
                      accept={CONFIG.SUPPORTED_FILE_TYPES.map(type => `.${type}`).join(',')}
                      onChange={(e) => setFile(e.target.files[0])}
                      className="hidden"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      {file ? (
                        <div>
                          <p className="text-green-600 font-semibold mb-2">{file.name}</p>
                          <p className="text-sm text-gray-500">Click to change file</p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-gray-700 font-semibold mb-2">Click to upload document</p>
                          <p className="text-sm text-gray-500">
                            Supported: {CONFIG.SUPPORTED_FILE_TYPES.join(', ')} (Max: {CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB)
                          </p>
                        </div>
                      )}
                    </label>
                  </div>

                  <button
                    onClick={() => setCurrentStep(2)}
                    disabled={!file}
                    className={`w-full py-3 px-4 rounded-xl font-semibold transition-all ${
                      file
                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Continue to Settings
                  </button>
                </div>
              )}

              {/* Step 2: Settings */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Configure Policy Settings</h3>
                    <p className="text-gray-600 mb-6">Set up heartbeat interval and quorum requirements</p>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <Clock className="w-4 h-4" />
                      Heartbeat Interval (seconds)
                    </label>
                    <input
                      type="number"
                      placeholder={`Default: ${CONFIG.DEFAULT_HEARTBEAT_INTERVAL} (24 hours)`}
                      value={interval}
                      onChange={(e) => setInterval(e.target.value)}
                      className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {Math.floor(interval / 3600)} hours - How often you need to send a heartbeat
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <Users className="w-4 h-4" />
                      Required Quorum
                    </label>
                    <input
                      type="number"
                      min="1"
                      placeholder={`Default: ${CONFIG.DEFAULT_QUORUM}`}
                      value={quorum}
                      onChange={(e) => setQuorum(e.target.value)}
                      className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Minimum number of beneficiaries needed to release the will
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => setCurrentStep(3)}
                      className="flex-1 py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                    >
                      Continue to Beneficiaries
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Beneficiaries */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Add Beneficiaries</h3>
                    <p className="text-gray-600 mb-6">Specify who can access your will</p>
                  </div>

                  <div className="space-y-3">
                    {beneficiaries.map((beneficiary, index) => (
                      <div key={index} className="flex gap-2">
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="0x..."
                            value={beneficiary}
                            onChange={(e) => updateBeneficiary(index, e.target.value)}
                            className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                          />
                        </div>
                        {beneficiaries.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeBeneficiary(index)}
                            className="p-3 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addBeneficiary}
                    className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-semibold transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Add Another Beneficiary
                  </button>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setCurrentStep(2)}
                      className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={registerPolicy}
                      disabled={isLoading || beneficiaries.every(b => !b.trim())}
                      className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
                        isLoading || beneficiaries.every(b => !b.trim())
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                      }`}
                    >
                      {isLoading ? 'Processing...' : 'Register Policy'}
                    </button>
                  </div>
                </div>
              )}

              {/* Status Message */}
              {status.message && (
                <div className={`mt-6 p-4 rounded-xl flex items-start gap-3 ${
                  status.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
                  status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
                  'bg-blue-50 text-blue-800 border border-blue-200'
                }`}>
                  {status.type === 'error' && <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                  {status.type === 'success' && <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                  <p>{status.message}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      </div>
    </>
  );
}
